import { MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SafeHtml } from "@/components/common/SafeHtml";
import { getProfilePictureUrl } from "@/features/auth/authService";
import { formatDateTime } from "@/lib/invoiceStatus";
import { getActionButtonClass } from "@/lib/semanticColors";
import { cn } from "@/lib/utils";

const PREVIEW_COUNT = 3;

function initialsOf(name) {
  if (!name) return "?";
  // `filter(Boolean)` before the slice, not after: a leading space makes
  // `split` emit an empty first part, which would otherwise take one of the two
  // slots and drop a real initial. Same rule as `getUserInitials`.
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return initials || "?";
}

/**
 * The comment affordance on a table row. It is absent when the invoice has no
 * comments — the icon is the signal that there is something to read, so showing
 * a permanently-disabled one on every row would say nothing.
 *
 * Hovering or focusing it previews the most recent few without leaving the
 * queue; activating it opens the invoice on its Comments tab.
 */
export function InvoiceCommentsPeek({ invoice, className }) {
  const comments = invoice?.comments ?? [];
  if (comments.length === 0) return null;

  const latest = [...comments]
    .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
    .slice(0, PREVIEW_COUNT);

  const shown = latest.length;
  const total = comments.length;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            nativeButton={false}
            render={
              <Link
                to={`/invoice/view/${invoice.id}`}
                state={{ tab: "comments" }}
                onClick={(event) => event.stopPropagation()}
              />
            }
            aria-label={`Read ${total} comment${total === 1 ? "" : "s"} on invoice ${invoice.invoiceNumber}`}
            className={cn("relative", className, getActionButtonClass("comment"))}
          />
        }
      >
        <MessageSquare className="size-3.5" />
        <span className="invoice-comment-count num" aria-hidden="true">
          {total > 9 ? "9+" : total}
        </span>
      </TooltipTrigger>

      <TooltipContent side="bottom" className="w-80 max-w-none flex-col items-stretch gap-0 p-0">
        <p className="border-b border-line px-3 py-2 text-xs font-medium text-muted-foreground">
          {shown === total
            ? `${total} comment${total === 1 ? "" : "s"}`
            : `Latest ${shown} of ${total} comments`}
        </p>
        <ul className="max-h-72 overflow-y-auto">
          {latest.map((comment) => (
            <li key={comment.guid} className="border-b border-line-subtle px-3 py-2 last:border-0">
              <div className="flex items-center gap-2">
                <Avatar size="sm" className="size-5">
                  <AvatarImage src={getProfilePictureUrl(comment.createdBy?.guid)} alt="" />
                  <AvatarFallback className="text-[0.5rem] font-semibold">
                    {initialsOf(comment.createdBy?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-xs font-medium text-foreground">
                  {comment.createdBy?.name ?? "Unknown"}
                </span>
                <time className="ms-auto shrink-0 text-xs text-muted-foreground" dateTime={comment.createdOn}>
                  {formatDateTime(comment.createdOn)}
                </time>
              </div>
              <SafeHtml
                html={comment.description}
                className="invoice-comment-peek-body mt-1 text-xs leading-relaxed text-muted-foreground"
              />
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
